import { FlowDefinition, FormDefinition } from "@/types/domain";
import {
  BindFormResponse,
  WorkflowListItemResponse,
  WorkflowListResponse,
  WorkflowResponse,
  WorkflowRevisionResponse,
} from "./response";
import { tFormRevisionSchema, tFormSchema } from "../form/transform";
import z from "zod";
import { FormStatus } from "@/types/form-builder";
import { EntityKey } from "@/types/form-builder";
import {
  ApplicantSource,
  ApprovalNodeType,
  ApproverType,
  ApproveMethod,
  ConditionBranch,
  ConditionNodeData,
  ConditionNodeType,
  EndNodeData,
  EndNodeType,
  FormNodeData,
  FormNodeType,
  ParallelApprovalNodeData,
  ParallelApprovalNodeType,
  SubflowNodeData,
  SubflowNodeType,
  WorkflowNode,
  WorkflowNodeKey,
  WorkflowEdgeKey,
  UiExpression,
  CodeExpression,
  VisibilityAction,
  VisibilityRule,
  Branch as fBranch,
  WorkflowEventLifecycle,
  WorkflowEventReference,
  WorkflowNodeEvents,
} from "@/types/flow";
import { Edge } from "@xyflow/react";
import {
  normalizeVisibilityRules,
  resolveEffectiveVisibilityRules,
} from "@/const/flow";

// const FORM_FIELD_REGEX = /\{\{\s*form\.([^.]+)\.value\s*\}\}/;

// const parseFormFieldReference = (field: string) => {
//   const match = field.match(FORM_FIELD_REGEX);
//   return match ? match[1] : field;
// };

const buildDepartmentSupervisorExpression = (formField?: string) => {
  if (!formField) return "";

  return `function expression(){
  return getFormField(${JSON.stringify(formField)}).value;
}`;
};

const parseBranchFields = (branch: Branch): ConditionBranch["branch"] => {
  if ("expression" in branch) {
    return {
      expression: branch.expression,
    };
  }
  if ("logic" in branch) {
    return {
      logic: branch.logic,
      left: parseBranchFields(branch.left) as fBranch,
      right: parseBranchFields(branch.right) as fBranch,
    };
  }
  return {
    ...branch,
    field: branch.field,
  };
};

type WorkflowEventPayloadReference = {
  id: string;
  name?: string;
  retry_on_failure?: boolean;
  halt_on_failure?: boolean;
  fire_on?: WorkflowEventReference["fire_on"];
};

type WorkflowEventPayloadLifecycle = {
  enter?: WorkflowEventPayloadReference[];
  leave?: WorkflowEventPayloadReference[];
};

type WorkflowNodeEventsPayload = {
  node?: WorkflowEventPayloadLifecycle;
  task?: WorkflowEventPayloadLifecycle;
};

type DistributiveOmit<T, K extends keyof any> = T extends unknown
  ? Omit<T, K>
  : never;

type NodeResponsePayload = DistributiveOmit<NodeResponse, "events"> & {
  events?: WorkflowNodeEventsPayload;
};

type FlowDefinitionPayload = Omit<FlowDefinitionResponse, "nodes"> & {
  nodes: NodeResponsePayload[];
};

const toWorkflowEventReference = (
  event: WorkflowEventPayloadReference | string,
): WorkflowEventReference | null => {
  if (typeof event === "string") {
    return { id: event, name: event };
  }
  if (!event.id) return null;
  return {
    id: event.id,
    name: event.name || event.id,
    retry_on_failure: event.retry_on_failure,
    halt_on_failure: event.halt_on_failure,
    fire_on: event.fire_on,
  };
};

const normalizeEventLifecycle = (
  events?: WorkflowEventLifecycle,
): WorkflowEventPayloadLifecycle | undefined => {
  const enter = Array.isArray(events?.enter)
    ? events.enter
        .map(toWorkflowEventReference)
        .filter((event): event is WorkflowEventReference => event !== null)
    : [];
  const leave = Array.isArray(events?.leave)
    ? events.leave
        .map(toWorkflowEventReference)
        .filter((event): event is WorkflowEventReference => event !== null)
    : [];

  if (enter.length === 0 && leave.length === 0) return undefined;

  return { enter, leave };
};

const normalizeNodeEvents = (
  events?: WorkflowNodeEvents,
): WorkflowNodeEventsPayload | undefined => {
  const node = normalizeEventLifecycle(events?.node);

  if (!node) return undefined;

  return { node };
};

const normalizeApprovalNodeEvents = (
  events?: WorkflowNodeEvents,
): WorkflowNodeEventsPayload | undefined => {
  const node = normalizeEventLifecycle(events?.node);
  const task = normalizeEventLifecycle(events?.task);

  if (!node && !task) return undefined;

  return {
    ...(node ? { node } : {}),
    ...(task ? { task } : {}),
  };
};

const deparseEventReferenceList = (
  events?: (WorkflowEventPayloadReference | string)[],
): WorkflowEventReference[] | undefined => {
  if (!Array.isArray(events)) return undefined;

  return events.map((event) =>
    typeof event === "string"
      ? { id: event, name: event }
      : {
          id: event.id,
          name: event.name ?? event.id,
          retry_on_failure: event.retry_on_failure,
          halt_on_failure: event.halt_on_failure,
          fire_on: event.fire_on,
        },
  );
};

const deparseEventLifecycle = (
  events?: WorkflowEventLifecycle | WorkflowEventPayloadLifecycle,
): WorkflowEventLifecycle | undefined => {
  if (!events) return undefined;

  return {
    ...(events.enter ? { enter: deparseEventReferenceList(events.enter) } : {}),
    ...(events.leave ? { leave: deparseEventReferenceList(events.leave) } : {}),
  };
};

const deparseNodeEvents = (
  events?: WorkflowNodeEvents | WorkflowNodeEventsPayload,
): WorkflowNodeEvents | undefined => {
  if (!events) return undefined;

  return {
    ...(events.node ? { node: deparseEventLifecycle(events.node) } : {}),
    ...(events.task ? { task: deparseEventLifecycle(events.task) } : {}),
  };
};

import {
  ApprovalNodeResponse,
  ApproverObjectResponse,
  ConditionNodeResponse,
  FlowDefinitionResponse,
  NodeResponse,
  Branch, // Import Branch
} from "./flow-definition";
import { FormResponse } from "../form/response";
import getConditionTreeName from "@/utils/get-tree-name";
import { tagSchema } from "../master-data/response";
import { tTag } from "../master-data/transform";
import is from "zod/v4/locales/is.cjs";
import { transformPaginatedResponse } from "../shared";

// Component visibility rules parser/deparser.
const isVisibilityAction = (action: string): action is VisibilityAction =>
  action === VisibilityAction.HIDE ||
  action === VisibilityAction.EDITABLE ||
  action === VisibilityAction.GRID_EDITABLE ||
  action === VisibilityAction.DISABLED ||
  action === VisibilityAction.REQUIRED;

export const parseComponentRulesForApi = (
  rules: VisibilityRule[] | undefined,
  allowedComponentNames?: Set<string>,
): Array<{
  component_name: string;
  column_key?: string;
  actions: string[];
  condition?: string;
}> => {
  if (!Array.isArray(rules)) return [];

  return rules.reduce<
    Array<{
      component_name: string;
      column_key?: string;
      actions: string[];
      condition?: string;
    }>
  >((acc, rule) => {
    if (!rule?.componentName) return acc;
    if (
      allowedComponentNames &&
      !allowedComponentNames.has(rule.componentName)
    ) {
      return acc;
    }

    const actions = Array.from(new Set(rule.actions ?? [])).filter(
      isVisibilityAction,
    );
    const apiActions = actions.map((action) =>
      action === VisibilityAction.GRID_EDITABLE
        ? VisibilityAction.EDITABLE
        : action,
    );

    if (!apiActions.length) return acc;
    acc.push({
      component_name: rule.componentName,
      ...(rule.columnKey ? { column_key: rule.columnKey } : {}),
      actions: apiActions.includes(VisibilityAction.HIDE)
        ? [VisibilityAction.HIDE]
        : Array.from(new Set(apiActions)),
      ...(rule.condition ? { condition: rule.condition } : {}),
    });
    return acc;
  }, []);
};

const deparseComponentRulesFromApi = (
  rules:
    | Array<{
        component_name: string;
        column_key?: string;
        actions: string[];
        condition?: string;
      }>
    | undefined,
  allowedComponentNames?: Set<string>,
  componentTypesByName?: Record<string, EntityKey>,
): VisibilityRule[] => {
  if (!Array.isArray(rules)) return [];

  return normalizeVisibilityRules(
    rules.reduce<VisibilityRule[]>((acc, rule) => {
      if (!rule?.component_name) return acc;
      if (
        allowedComponentNames &&
        !allowedComponentNames.has(rule.component_name)
      ) {
        return acc;
      }

      const actions = Array.from(new Set(rule.actions ?? []))
        .map((action) => {
          if (action === "disable") {
            return VisibilityAction.DISABLED;
          }

          if (
            action === VisibilityAction.EDITABLE &&
            !rule.column_key &&
            componentTypesByName?.[rule.component_name] === EntityKey.grid
          ) {
            return VisibilityAction.GRID_EDITABLE;
          }

          return action;
        })
        .filter(isVisibilityAction);

      if (!actions.length) return acc;

      acc.push({
        componentName: rule.component_name,
        ...(rule.column_key ? { columnKey: rule.column_key } : {}),
        actions: actions.includes(VisibilityAction.HIDE)
          ? [VisibilityAction.HIDE]
          : actions,
        ...(rule.condition ? { condition: rule.condition } : {}),
      });
      return acc;
    }, []),
  );
};

const getAllowedComponentNamesFromFormSchema = (
  schema?: FormDefinition["schema"],
): Set<string> | undefined => {
  if (!schema?.entities) return undefined;

  const names = new Set<string>();
  Object.values(schema.entities).forEach((entity) => {
    const attributes = (entity?.attributes ?? {}) as Record<string, unknown>;
    const name = attributes.name;
    if (typeof name === "string" && name.trim().length > 0) {
      names.add(name.trim());
    }
  });

  return names;
};

const getAllowedComponentNamesFromBindingForm = (
  bindingForm?: FormResponse,
): Set<string> | undefined => {
  const entities = bindingForm?.revision?.form_schema?.entities;
  if (!entities) return undefined;

  const names = new Set<string>();
  Object.entries(entities).forEach(([entityKey, entity]) => {
    if (entityKey) names.add(entityKey);

    const attributes = ((entity as { attributes?: Record<string, unknown> })
      .attributes ?? {}) as Record<string, unknown> | undefined;
    const name = attributes?.name;
    if (typeof name === "string" && name.trim().length > 0) {
      names.add(name.trim());
    }
  });

  return names;
};

const getComponentTypesByNameFromBindingForm = (
  bindingForm?: FormResponse,
): Record<string, EntityKey> | undefined => {
  const entities = bindingForm?.revision?.form_schema?.entities;
  if (!entities) return undefined;

  const componentTypesByName: Record<string, EntityKey> = {};
  Object.entries(entities).forEach(([entityKey, entity]) => {
    if (entityKey && entity.type) {
      componentTypesByName[entityKey] = entity.type as EntityKey;
    }

    const attributes = ((entity as { attributes?: Record<string, unknown> })
      .attributes ?? {}) as Record<string, unknown> | undefined;
    const name = attributes?.name;
    if (typeof name === "string" && name.trim().length > 0 && entity.type) {
      componentTypesByName[name.trim()] = entity.type as EntityKey;
    }
  });

  return componentTypesByName;
};

const getAllowedComponentNamesFromWorkflowNodes = (
  nodes: WorkflowNode[],
): Set<string> | undefined => {
  const formNode = nodes.find((node) => node.type === WorkflowNodeKey.Form);
  const formSchema = (formNode?.data as FormNodeType["data"] | undefined)?.form
    ?.schema;
  return getAllowedComponentNamesFromFormSchema(formSchema);
};

const getWorkflowFormSchema = (
  nodes: WorkflowNode[],
): FormDefinition["schema"] | undefined => {
  const formNode = nodes.find((node) => node.type === WorkflowNodeKey.Form);
  return (formNode?.data as FormNodeType["data"] | undefined)?.form?.schema;
};

// =================================================================
// Frontend -> Backend Parsers
// =================================================================

/**
 * Recursively sanitizes a frontend branch structure to conform to the backend schema
 * by converting `undefined` to `null`.
 * @param branch The frontend branch object.
 * @returns A sanitized branch object or null.
 */
function sanitizeBranch(branch: ConditionBranch["branch"]): Branch | null {
  if (!branch) {
    return null;
  }

  if ("expression" in branch) {
    return {
      expression: branch.expression,
    };
  }

  // Check if it's a complex ConditionNode
  if ("logic" in branch) {
    const left = sanitizeBranch(branch.left);
    const right = sanitizeBranch(branch.right);

    // If either side is null after sanitizing, the complex node is invalid for the backend.
    if (left === null || right === null) {
      return null;
    }

    return {
      logic: branch.logic,
      left: left,
      right: right,
    };
  }

  // It's a simple Expression
  const expression = branch as UiExpression;

  const isStringOperator =
    expression.operator === "contains" ||
    expression.operator === "not_contains" ||
    expression.operator === "equals" ||
    expression.operator === "not_equals";

  if (!isStringOperator) {
    // Convert to number

    return {
      ...expression,
      field: `${expression.field}`,
      value: Number(expression.value),
    };
  } else {
    return {
      ...expression,

      field: `${expression.field}`,
      value: String(expression.value),
    };
  }
}

/**
 * Converts a single frontend ApprovalNodeData to a backend ApproverObjectPayload.
 * This is a placeholder implementation and needs to be filled out with actual logic.
 * @param approvalData - The frontend approval node data.
 * @returns The backend approver object payload.
 */
function parseSingleApprover(
  approvalData: ApprovalNodeType["data"],
): ApproverObjectResponse {
  const ensureInt = (value?: string | number | null): number | undefined => {
    if (value === undefined || value === null) {
      return undefined;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.trunc(value);
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return Math.trunc(parsed);
      }
    }
    return undefined;
  };

  const isJobGradeMethod = (
    method: ApproveMethod | undefined,
  ): method is ApproveMethod & { jobGrade: number } =>
    Boolean(method && "jobGrade" in method);

  const isApproveLevelMethod = (
    method: ApproveMethod | undefined,
  ): method is ApproveMethod & { approveLevel: number } =>
    Boolean(method && "approveLevel" in method);

  const buildHierarchyConfig = (method?: ApproveMethod, advanced?: string) => {
    let hierarchyMethod: "to_job_grade" | "to_level" = "to_job_grade";
    let jobGrade: number | undefined;
    let level: number | undefined;

    if (isJobGradeMethod(method)) {
      hierarchyMethod = "to_job_grade";
      jobGrade = method.jobGrade;
    } else if (isApproveLevelMethod(method)) {
      hierarchyMethod = "to_level";
      level = method.approveLevel;
    } else {
      jobGrade = 0;
    }

    const config: {
      method: "to_job_grade" | "to_level";
      job_grade?: number;
      level?: number;
      org_reference_field?: string;
    } = { method: hierarchyMethod };

    if (jobGrade !== undefined) {
      config.job_grade = jobGrade;
    }
    if (level !== undefined) {
      config.level = level;
    }
    if (advanced) {
      config.org_reference_field = advanced;
    }

    return config;
  };

  switch (approvalData.approver) {
    case ApproverType.Applicant:
      return {
        type: "applicant",
        config: {},
        description: approvalData.description,
        reuse_prior_approvals: approvalData.shouldSkip ?? true,
      };

    case ApproverType.ApplicantReportLine:
      return {
        type: "applicant_reporting_line",
        config: buildHierarchyConfig(
          approvalData.approveMethod,
          approvalData.advancedSetting,
        ),
        description: approvalData.description,
        reuse_prior_approvals: approvalData.shouldSkip ?? true,
      };

    case ApproverType.UserReportLine: {
      const specificUser = approvalData.specificUser;
      const hasExplicitType = specificUser && "type" in specificUser;
      const manualUserCode =
        specificUser?.type === "manual"
          ? (specificUser.user?.code ??
            (specificUser.userId && ensureInt(specificUser.userId) === undefined
              ? specificUser.userId
              : undefined))
          : undefined;
      const manualUserId =
        specificUser?.type === "manual"
          ? ensureInt(specificUser.user?.id ?? (specificUser as any).userId)
          : !hasExplicitType
            ? ensureInt((specificUser as any)?.userId)
            : undefined;

      const source: "manual" | "expression" = hasExplicitType
        ? specificUser!.type === "reference"
          ? "expression"
          : "manual"
        : manualUserCode !== undefined || manualUserId !== undefined
          ? "manual"
          : "expression";

      const config: {
        source: "manual" | "expression";
        user_code?: string;
        user_id?: number;
        expression?: string;
        method: "to_job_grade" | "to_level";
        job_grade?: number;
        level?: number;
        org_reference_field?: string;
      } = {
        source,
        ...buildHierarchyConfig(
          approvalData.approveMethod,
          approvalData.advancedSetting,
        ),
      };

      if (source === "manual") {
        if (manualUserCode !== undefined) {
          config.user_code = manualUserCode;
        } else if (manualUserId !== undefined) {
          config.user_id = manualUserId;
        }
      } else {
        const expressionValue =
          specificUser?.type === "reference"
            ? (specificUser.reference ?? specificUser.userId ?? "")
            : (specificUser?.userId ?? "");
        config.expression = expressionValue;
      }

      return {
        type: "specific_user_reporting_line",
        config,
        description: approvalData.description,
        reuse_prior_approvals: approvalData.shouldSkip ?? true,
      };
    }
    case ApproverType.DepartmentSupervisor: {
      const departmentSupervisor = approvalData.departmentSupervisor;
      let source: "manual" | "expression" = "manual";
      let orgUnitCode: string | undefined;
      let orgUnitId: number | undefined;
      let expression: string | undefined;

      if (departmentSupervisor?.type === "manual") {
        source = "manual";
        orgUnitCode =
          departmentSupervisor.department?.code ??
          (departmentSupervisor.departmentId &&
          ensureInt(departmentSupervisor.departmentId) === undefined
            ? departmentSupervisor.departmentId
            : undefined);
        orgUnitId = ensureInt(
          departmentSupervisor.department?.id ??
            departmentSupervisor.departmentId,
        );
      } else if (departmentSupervisor?.type === "reference") {
        source = "expression";
        expression = departmentSupervisor.reference ?? "";
      } else {
        orgUnitId = 0;
      }

      const config: {
        source: "manual" | "expression";
        org_unit_code?: string;
        org_unit_id?: number;
        expression?: string;
      } = { source };

      if (source === "manual") {
        if (orgUnitCode !== undefined) {
          config.org_unit_code = orgUnitCode;
        } else if (orgUnitId !== undefined) {
          config.org_unit_id = orgUnitId;
        }
      } else {
        config.expression = expression ?? "";
      }

      return {
        type: "department_head",
        config,
        description: approvalData.description,
        reuse_prior_approvals: approvalData.shouldSkip ?? true,
      };
    }
    case ApproverType.Role: {
      const specificRole = approvalData.specificRole;
      const isExpressionSource = specificRole?.type === "reference";
      const expressionValue = isExpressionSource
        ? (specificRole?.reference ?? "")
        : "";

      // Backward compat: support both roleIds (multi) and roleId (single)
      const manualRole = specificRole as
        | {
            type: "manual";
            roleIds?: string[];
            roleId?: string;
            roles?: { id: string; code: string }[];
            role?: { id: string; code: string };
          }
        | undefined;
      const manualRoleValues = manualRole?.roleIds?.length
        ? manualRole.roleIds
        : manualRole?.roleId
          ? [manualRole.roleId]
          : [];
      const roleCodesFromValues = manualRoleValues.filter(
        (value): value is string => ensureInt(value) === undefined,
      );
      const roleIdsFromValues = manualRoleValues
        .map(ensureInt)
        .filter((val): val is number => val !== undefined);
      const roleCodesFromObjects = manualRole?.roles?.length
        ? manualRole.roles.map((role) => role.code)
        : manualRole?.role?.code
          ? [manualRole.role.code]
          : [];
      const roleIdsFromObjects = manualRole?.roles?.length
        ? manualRole.roles
            .map((r) => ensureInt(r.id))
            .filter((val): val is number => val !== undefined)
        : manualRole?.role?.id
          ? [ensureInt(manualRole.role.id)].filter(
              (val): val is number => val !== undefined,
            )
          : [];
      const parsedRoleCodes = Array.from(
        new Set([...roleCodesFromObjects, ...roleCodesFromValues]),
      ).filter(Boolean);
      const parsedRoleIds = Array.from(
        new Set([...roleIdsFromObjects, ...roleIdsFromValues]),
      );

      return {
        type: "role",
        config: isExpressionSource
          ? {
              source: "expression",
              expression: expressionValue,
            }
          : {
              source: "manual",
              ...(parsedRoleCodes.length > 0
                ? {
                    role_codes: parsedRoleCodes,
                  }
                : parsedRoleIds.length > 0
                  ? {
                      role_ids: parsedRoleIds,
                    }
                  : {}),
            },
        description: approvalData.description,
        reuse_prior_approvals: approvalData.shouldSkip ?? true,
      };
    }

    case ApproverType.User: {
      const specificUser = approvalData.specificUser;
      const isExpressionSource = specificUser?.type === "reference";
      const expressionValue = isExpressionSource
        ? (specificUser?.reference ?? specificUser?.userId ?? "")
        : "";

      const manualUser = specificUser as
        | {
            type: "manual";
            userIds?: string[];
            users?: { id: string; code: string }[];
          }
        | undefined;
      const manualUserValues = manualUser?.userIds ?? [];
      const userCodesFromValues = manualUserValues.filter(
        (value): value is string => ensureInt(value) === undefined,
      );
      const userIdsFromValues = manualUserValues
        .map(ensureInt)
        .filter((val): val is number => val !== undefined);
      const userCodesFromObjects =
        manualUser?.users?.map((user) => user.code) ?? [];
      const userIdsFromObjects =
        manualUser?.users
          ?.map((user) => ensureInt(user.id))
          .filter((val): val is number => val !== undefined) ?? [];
      const parsedUserCodes = Array.from(
        new Set([...userCodesFromObjects, ...userCodesFromValues]),
      ).filter(Boolean);
      const parsedUserIds = Array.from(
        new Set([...userIdsFromObjects, ...userIdsFromValues]),
      );

      return {
        type: "specific_users",
        config: isExpressionSource
          ? {
              source: "expression",
              expression: expressionValue,
            }
          : {
              source: "manual",
              ...(parsedUserCodes.length > 0
                ? { user_codes: parsedUserCodes }
                : parsedUserIds.length > 0
                  ? { user_ids: parsedUserIds }
                  : {}),
            },
        description: approvalData.description,
        reuse_prior_approvals: approvalData.shouldSkip ?? true,
      };
    }

    default:
      return {
        type: "applicant",
        config: {},
        description: undefined,
        reuse_prior_approvals: true,
      };
  }
}

// =================================================================
// Backend -> Frontend Deparsers
// =================================================================

function deparseSingleApprover(
  singleApprover: ApproverObjectResponse,
): ApprovalNodeType["data"] {
  let data: ApprovalNodeType["data"];

  const buildApproveMethod = (method: {
    method: "to_job_grade" | "to_level";
    job_grade?: number;
    level?: number;
  }): ApproveMethod => {
    if (method.method === "to_job_grade") {
      return {
        method: "to_job_grade",
        jobGrade: method.job_grade ?? 0,
      };
    } else {
      // method.method === 'to_level'
      return {
        method: "to_level",
        approveLevel: method.level ?? 0,
      };
    }
  };

  switch (singleApprover.type) {
    case "applicant":
      data = {
        description: singleApprover.description,
        approver: ApproverType.Applicant,
        shouldSkip: singleApprover.reuse_prior_approvals ?? true,
        next: null,
        parents: null,
      };
      break;

    case "applicant_reporting_line": {
      const config = singleApprover.config;
      data = {
        description: singleApprover.description,
        approver: ApproverType.ApplicantReportLine,
        approveMethod: buildApproveMethod(config),
        shouldSkip: singleApprover.reuse_prior_approvals ?? true,
        advancedSetting: config?.org_reference_field ?? "",
        next: null,
        parents: null,
      };
      break;
    }

    case "specific_user_reporting_line": {
      const config = singleApprover.config;
      const isExpressionSource =
        config?.source === "expression" || config?.source === "form_field";
      const expressionValue =
        (config as { expression?: string; form_field?: string }).expression ??
        (config as { expression?: string; form_field?: string }).form_field ??
        "";
      data = {
        description: singleApprover.description,
        approver: ApproverType.UserReportLine,
        approveMethod: buildApproveMethod(config),
        shouldSkip: singleApprover.reuse_prior_approvals ?? true,
        advancedSetting: config?.org_reference_field ?? "",
        specificUser: isExpressionSource
          ? {
              type: "reference" as const,
              userId: expressionValue,
              reference: expressionValue,
            }
          : {
              type: "manual" as const,
              userId:
                config?.user_code ??
                (config?.user_id !== undefined ? String(config.user_id) : ""),
              user: undefined,
            },
        next: null,
        parents: null,
      };
      break;
    }

    case "department_head": {
      const config = singleApprover.config;
      data = {
        description: singleApprover.description,
        approver: ApproverType.DepartmentSupervisor,
        shouldSkip: singleApprover.reuse_prior_approvals ?? true,
        departmentSupervisor:
          config?.source === "manual"
            ? {
                type: "manual",
                departmentId:
                  config?.org_unit_code ??
                  (config?.org_unit_id !== undefined
                    ? String(config.org_unit_id)
                    : ""),
              }
            : {
                type: "reference",
                reference:
                  config?.expression ??
                  buildDepartmentSupervisorExpression(config?.form_field),
              },
        next: null,
        parents: null,
      };
      break;
    }

    case "role": {
      const config = singleApprover.config;
      const isExpressionSource = config?.source === "expression";

      const roleIds = config?.role_codes?.length
        ? [...config.role_codes]
        : config?.role_code
          ? [config.role_code]
          : config?.role_ids
            ? (config.role_ids as number[]).map(String)
            : config?.role_id !== undefined
              ? [String(config.role_id)]
              : [];

      data = {
        description: singleApprover.description,
        approver: ApproverType.Role,
        shouldSkip: singleApprover.reuse_prior_approvals ?? true,
        specificRole: isExpressionSource
          ? {
              type: "reference" as const,
              reference: (config?.expression as string) ?? "",
            }
          : {
              type: "manual" as const,
              roleIds,
            },
        next: null,
        parents: null,
      };
      break;
    }

    case "specific_users": {
      const config = singleApprover.config;
      const isExpressionSource =
        config?.source === "expression" || config?.source === "form_field";
      data = {
        description: singleApprover.description,
        approver: ApproverType.User,
        shouldSkip: singleApprover.reuse_prior_approvals ?? true,
        specificUser: isExpressionSource
          ? {
              type: "reference",
              userId: config?.expression ?? config?.form_field ?? "",
              reference: config?.expression ?? config?.form_field ?? "",
            }
          : {
              type: "manual",
              userIds: config?.user_codes?.length
                ? [...config.user_codes]
                : (config?.user_ids ?? []).map(String),
            },
        next: null,
        parents: null,
      };
      break;
    }

    default:
      data = {
        // description: approvalNode.description,
        approver: ApproverType.Applicant,
        next: null,
        parents: null,
      };
  }
  return {
    ...data,
    ...(singleApprover.events
      ? { events: deparseNodeEvents(singleApprover.events) }
      : {}),
  };
}

/**
 * Converts a backend node definition to a frontend React Flow node and its edges.
 * @param backendNode - The backend node object.
 * @returns An object containing the frontend node and any outgoing edges.
 */
function deparseNode(
  backendNode: NodeResponse,
  allowedComponentNames?: Set<string>,
  componentTypesByName?: Record<string, EntityKey>,
): {
  node: WorkflowNode;
  edges: Edge[];
} {
  const position = { x: 0, y: 0 }; // Default position
  const edges: Edge[] = [];

  switch (backendNode.type) {
    case "start": {
      const startRules = deparseComponentRulesFromApi(
        backendNode.component_rules,
        allowedComponentNames,
        componentTypesByName,
      );
      const applicantSource: ApplicantSource =
        backendNode.applicant_source === "submitter"
          ? "submitter"
          : "selection";
      const node: FormNodeType = {
        id: backendNode.key,
        type: WorkflowNodeKey.Form,
        position,
        data: {
          description: backendNode.description,
          next: backendNode.next ?? null,
          parents: null,
          applicantSource,
          allowDiscard: backendNode.allow_discard ?? true,
          ...(backendNode.events
            ? { events: deparseNodeEvents(backendNode.events) }
            : {}),
          ...(startRules.length ? { componentRules: startRules } : {}),
        },
      };
      return { node, edges };
    }
    case "approval": {
      const approvalNode = backendNode as ApprovalNodeResponse;
      const approvalNodeRules = deparseComponentRulesFromApi(
        approvalNode.component_rules,
        allowedComponentNames,
        componentTypesByName,
      );
      if (approvalNode.approval_method === "single") {
        const singleApprover = approvalNode.approvers as ApproverObjectResponse;
        const data = deparseSingleApprover(singleApprover);
        const approverRules = deparseComponentRulesFromApi(
          singleApprover.component_rules,
          allowedComponentNames,
          componentTypesByName,
        );
        // New API spec stores rules inside approvers. Keep node-level fallback for older payloads.
        const componentRules =
          approverRules.length > 0 ? approverRules : approvalNodeRules;
        const node: ApprovalNodeType = {
          id: approvalNode.key,
          type: WorkflowNodeKey.Approval,
          position,
          data: {
            ...data,
            next: approvalNode.next ?? null,
            parents: null,
            ...(approvalNode.events
              ? { events: deparseNodeEvents(approvalNode.events) }
              : {}),
            ...(componentRules.length ? { componentRules } : {}),
          },
        };
        return { node, edges };
      } else {
        // Parallel
        const parallelApprovers =
          approvalNode.approvers as ApproverObjectResponse[];
        const approvals = parallelApprovers.map((approver) => {
          console.debug(
            "here 2",
            deparseSingleApprover(approver),
            approver,
            approvalNode,
          );
          const approverRules = deparseComponentRulesFromApi(
            approver.component_rules,
            allowedComponentNames,
            componentTypesByName,
          );
          return {
            ...deparseSingleApprover(approver),
            ...(approverRules.length ? { componentRules: approverRules } : {}),
            // description: approver.description,
            // shouldSkip: approver.reuse_prior_approvals ?? true,
          } satisfies ApprovalNodeType["data"];
        });

        const node: ParallelApprovalNodeType = {
          id: approvalNode.key,
          type: WorkflowNodeKey.ParallelApproval,
          position,
          data: {
            description: approvalNode.description,
            logic: approvalNode.approval_logic === "AND" ? "and" : "or",
            selectedApprovalIndex: null,
            approvals: approvals,
            ...(approvalNode.events
              ? { events: deparseNodeEvents(approvalNode.events) }
              : {}),
            next: approvalNode.next ?? null,
            parents: null,
          },
        };
        return { node, edges };
      }
    }

    case "condition": {
      const conditionNode = backendNode as ConditionNodeResponse;
      const node: ConditionNodeType = {
        id: conditionNode.key,
        type: WorkflowNodeKey.Condition,
        position,
        data: {
          description: conditionNode.description,
          conditions: conditionNode.conditions.map((c) => {
            const newBranch = c.branch
              ? parseBranchFields(c.branch)
              : undefined;
            const isExpression = Boolean(
              newBranch && "expression" in newBranch,
            );
            return {
              name:
                c.branch === null
                  ? "fallback-branch"
                  : isExpression
                    ? (newBranch as CodeExpression).expression
                    : getConditionTreeName(newBranch),
              isExpression,
              branch: newBranch,
              next: c.next,
            } as ConditionBranch;
          }),
          next: backendNode.next ?? null,
          parents: null,
          ...(conditionNode.events
            ? { events: deparseNodeEvents(conditionNode.events) }
            : {}),
        },
      };
      console.debug({ node });
      return { node, edges };
    }

    case "subflow": {
      const node: SubflowNodeType = {
        id: backendNode.key,
        type: WorkflowNodeKey.Subflow,
        position,
        data: {
          description: backendNode.description,
          workflowId: backendNode.subflow_id,
          workflow: null,
          next: backendNode.next ?? null,
          parents: null,
          ...(backendNode.events
            ? { events: deparseNodeEvents(backendNode.events) }
            : {}),
        },
      };
      return { node, edges };
    }

    case "end": {
      const node: EndNodeType = {
        id: backendNode.key,
        type: WorkflowNodeKey.End,
        position,
        data: {
          description: backendNode.description,
          next: null,
          parents: null,
          ...(backendNode.events
            ? { events: deparseNodeEvents(backendNode.events) }
            : {}),
        },
      };
      return { node, edges };
    }
  }
}

/**
 * Converts a backend flow definition into frontend nodes and edges.
 * @param data - The backend flow definition payload.
 * @returns An object containing the arrays of frontend nodes and edges.
 */
export function deparseFlow(
  data: FlowDefinitionResponse,
  bindingForm?: FormResponse,
): {
  nodes: WorkflowNode[];
  edges: Edge[];
} {
  const allNodes: WorkflowNode[] = [];
  const allowedComponentNames =
    getAllowedComponentNamesFromBindingForm(bindingForm);
  const componentTypesByName =
    getComponentTypesByNameFromBindingForm(bindingForm);

  data.nodes.forEach((backendNode) => {
    const { node, edges } = deparseNode(
      backendNode,
      allowedComponentNames,
      componentTypesByName,
    );
    allNodes.push(node);
  });

  if (bindingForm) {
    console.debug({ bindingForm });
    const formNode = allNodes.find(
      (node) => node.type === WorkflowNodeKey.Form,
    );

    if (formNode) {
      formNode.data.form = tFormSchema(bindingForm);
    }
  }

  const nodeIds = new Set(allNodes.map((n) => n.id));
  const derivedEdges: Edge[] = [];

  allNodes.forEach((node) => {
    if (node.type === WorkflowNodeKey.Condition) {
      const data = node.data as ConditionNodeData;
      data.conditions?.forEach((condition, index) => {
        if (condition.next && nodeIds.has(condition.next)) {
          derivedEdges.push({
            id: `${node.id}-condition-${index}-to-${condition.next}`,
            source: node.id,
            target: condition.next,
            type: WorkflowEdgeKey.Label,
            data: {
              label:
                condition.name === "fallback-branch"
                  ? "Fallback"
                  : condition.name,
              index,
            },
            sourceHandle: "bottom",
            targetHandle: "top",
          });
        }
      });
      return;
    }

    const nextNodeId = node.data.next;
    if (nextNodeId && nodeIds.has(nextNodeId)) {
      derivedEdges.push({
        id: `${node.id}-to-${nextNodeId}`,
        source: node.id,
        target: nextNodeId,
        type: WorkflowEdgeKey.Label,
        sourceHandle: "bottom",
        targetHandle: "top",
      });
    }
  });

  const parentMap = new Map<string, Set<string>>();
  derivedEdges.forEach((edge) => {
    const targetSet = parentMap.get(edge.target) ?? new Set<string>();
    targetSet.add(edge.source);
    parentMap.set(edge.target, targetSet);
  });

  const nodesWithParents = allNodes.map((node) => {
    const parents = Array.from(parentMap.get(node.id) ?? []);
    return {
      ...node,
      data: {
        ...node.data,
        parents,
      },
    } as WorkflowNode;
  });

  return { nodes: nodesWithParents, edges: derivedEdges };
}

/**
 * Converts frontend nodes and edges to the backend's flow definition format.
 * @param nodes - The array of frontend workflow nodes.
 * @param edges - The array of edges connecting the nodes.
 * @returns A backend-compatible flow definition payload.
 */
export function parseFlow(
  nodes: WorkflowNode[],
  _edges: Edge[],
): FlowDefinitionPayload {
  const allowedComponentNames =
    getAllowedComponentNamesFromWorkflowNodes(nodes);
  const workflowFormSchema = getWorkflowFormSchema(nodes);
  const backendNodes: NodeResponsePayload[] = nodes
    .map((node): NodeResponsePayload | null => {
      switch (node.type) {
        case WorkflowNodeKey.Form: {
          const formData = node.data as FormNodeType["data"];
          const applicantSource = formData.applicantSource;
          const events = normalizeNodeEvents(formData.events);
          return {
            key: node.id,
            type: "start",
            next: node.data.next ?? "end", // Default to 'end' if no next node
            description: node.data.description,
            ...(events ? { events } : {}),
            component_rules: parseComponentRulesForApi(
              resolveEffectiveVisibilityRules(
                formData.componentRules,
                formData.form?.schema ?? workflowFormSchema,
                "form",
              ),
              allowedComponentNames,
            ),
            allow_discard: formData.allowDiscard ?? true,
            ...(applicantSource === "submitter"
              ? { applicant_source: "submitter" as const }
              : { applicant_source: "selection" as const }),
          };
        }

        case WorkflowNodeKey.Approval: {
          const approvalData = node.data as ApprovalNodeType["data"];
          const approver = parseSingleApprover(approvalData);
          const events = normalizeApprovalNodeEvents(approvalData.events);
          return {
            key: node.id,
            type: "approval",
            next: approvalData.next ?? undefined,
            // description: node.data.description,
            approval_method: "single",
            ...(events ? { events } : {}),
            approvers: {
              ...approver,
              component_rules: parseComponentRulesForApi(
                resolveEffectiveVisibilityRules(
                  approvalData.componentRules,
                  workflowFormSchema,
                  "approval",
                ),
                allowedComponentNames,
              ),
            },
          };
        }

        case WorkflowNodeKey.ParallelApproval: {
          const parallelData = node.data as ParallelApprovalNodeData;
          const events = normalizeApprovalNodeEvents(parallelData.events);
          console.debug({ parallelData });
          return {
            key: node.id,
            type: "approval",
            next: parallelData.next ?? undefined,
            description: node.data.description,
            approval_method: "parallel",
            approval_logic: parallelData.logic === "and" ? "AND" : "OR",
            ...(events ? { events } : {}),
            approvers: parallelData.approvals.map((approval) => {
              const approvalEvents = normalizeApprovalNodeEvents(
                approval.events,
              );
              return {
                ...parseSingleApprover(approval),
                ...(approvalEvents ? { events: approvalEvents } : {}),
                // description: approval.description,
                // reuse_prior_approvals: approval.shouldSkip,
                component_rules: parseComponentRulesForApi(
                  resolveEffectiveVisibilityRules(
                    approval.componentRules,
                    workflowFormSchema,
                    "approval",
                  ),
                  allowedComponentNames,
                ),
              };
            }),
          };
        }

        case WorkflowNodeKey.Condition: {
          const conditionData = node.data as ConditionNodeData;
          const events = normalizeNodeEvents(conditionData.events);
          console.debug({ conditionData });

          return {
            key: node.id,
            type: "condition",
            description: node.data.description,
            ...(events ? { events } : {}),
            conditions: conditionData.conditions.map((cond) => {
              return {
                branch: sanitizeBranch(cond.branch),
                next: cond.next ?? "end",
              };
            }),
          };
        }

        case WorkflowNodeKey.Subflow: {
          const subflowData = node.data as SubflowNodeData;
          const events = normalizeNodeEvents(subflowData.events);
          return {
            key: node.id,
            type: "subflow",
            next: subflowData.next ?? "",
            description: node.data.description,
            ...(events ? { events } : {}),
            subflow_id: subflowData.workflowId ?? "",
          };
        }

        case WorkflowNodeKey.End: {
          const endData = node.data as EndNodeData;
          const events = normalizeNodeEvents(endData.events);
          return {
            key: node.id,
            type: "end",
            description: node.data.description,
            ...(events ? { events } : {}),
            next: undefined,
          };
        }

        // Placeholder and Dummy nodes are not sent to the backend
        default:
          return null;
      }
    })
    .filter((node): node is NodeResponsePayload => node !== null);

  return {
    version: 1, // Default version
    nodes: backendNodes,
  };
}

// =================================================================
// Main Transform Functions
// =================================================================

export function tWorkflowSchema(data: WorkflowResponse): FlowDefinition {
  const { nodes, edges } = data.revision.flow_definition
    ? deparseFlow(data.revision.flow_definition, data.bindingForm)
    : { nodes: [], edges: [] };
  return {
    id: data.workflow_id,
    revisionId: data.revision.revision_id,
    major: data.revision.major,
    minor: data.revision.minor,
    latestMajor: data.latestMajor,
    name: data.revision.name,
    description: data.revision.description ?? "",
    tags: z.array(tagSchema.transform(tTag)).parse(data.tags),
    version: data.revision.version,
    nodes,
    edges,
    createdAt: data.revision.created_at,
    updatedAt: "", // Backend doesn't provide top-level updatedAt
    publishStatus: data.is_active ? FormStatus.Published : FormStatus.Draft,
    serialPrefix: data.serial_prefix ?? "APP",
  };
}

export function tWorkflowListItem(
  data: WorkflowListItemResponse,
): FlowDefinition {
  return {
    id: data.workflow_id,
    revisionId: data.revisionId,
    major: data.major,
    minor: data.minor,
    name: data.name,
    description: data.description ?? "", // Not available in list item response
    tags: z.array(tagSchema.transform(tTag)).parse(data.tags),
    version: 1, // Not available in list item response
    nodes: [], // Not available in list item response
    edges: [], // Not available in list item response
    createdAt: data.created_at,
    updatedAt: "",
    publishStatus: data.is_active ? FormStatus.Published : FormStatus.Draft,
    serialPrefix: "APP",
  };
}

export function tWorkflowList(data: WorkflowListResponse) {
  return transformPaginatedResponse(data, tWorkflowListItem);
}
export function tWorkflowRevisionSchema(
  data: WorkflowRevisionResponse,
): FlowDefinition {
  const { nodes, edges } = data.flow_definition?.nodes
    ? deparseFlow(data.flow_definition)
    : { nodes: [], edges: [] };

  return {
    id: data.workflow_id,
    revisionId: data.revision_id,
    major: data.major,
    minor: data.minor,
    name: data.name,
    description: data.description ?? "",
    tags: [],
    version: data.version,
    nodes: nodes,
    edges: edges,
    createdAt: data.created_at,
    updatedAt: "",
    publishStatus:
      data.status === "ACTIVE" ? FormStatus.Published : FormStatus.Draft,
    serialPrefix: "APP",
  };
}

export function tBindFormSchema(data: BindFormResponse): FormDefinition {
  return tFormRevisionSchema(data.formRevision);
}
